import React, { type ComponentType, type ReactNode } from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { css } from 'styled-components';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { fieldBorderRadius } from '../../field-active/styled.tsx';
import NoActionSvg from '../field-toolbar-no-action/assets/no-action-svg.tsx';

type Props = {
	isDraggingOver?: boolean;
	dropHighlight?: boolean;
	header: string;
	description: string;
};

/*
 * View without the Drag&Drop decorators is in separate component to enable easy testing
 */
const FieldToolbarOverlay = (props: Props) => {
	const { header, description, isDraggingOver, dropHighlight } = props;

	return (
		<Container>
			<ColorsRemove isDraggingOver={isDraggingOver} dropHighlight={dropHighlight}>
				<MarginTop />
				<Content>
					<Column>
						<ImgWrapper>
							<NoActionSvg />
						</ImgWrapper>
						{/* eslint-disable-next-line @atlaskit/design-system/use-heading */}
						<h5>{header}</h5>
						<Description>{description}</Description>
					</Column>
				</Content>
				<MarginBottom />
			</ColorsRemove>
		</Container>
	);
};

FieldToolbarOverlay.defaultProps = {
	isDraggingOver: false,
	dropHighlight: true,
};

const marginMax = 10 * gridSize;

const dropzoneBorderColor = token('color.border.brand');
const dropzoneBackgroundColor = token('color.background.selected');
const dropzoneBackgroundHoverColor = token('color.background.selected.hovered');

const transitionSpeed = '0.3s';

export default FieldToolbarOverlay;

/*
 * CSS does not have max-margin so we have to fake it with flexbox grow elements
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MarginBottom = styled.div<{
	children?: ReactNode;
}>({
	flexGrow: 1,
});

/*
 * Content must be display: block so that the content height is computed properly.
 *
 * !! CAREFUL HERE !!
 *
 * ^^^^^^^^^^^^^^^^^^
 *
 * As this is somehow brittle and introducing overflow or display or position property breaks the vertical shrinking.
 * Always make sure to see the storybooks after changing this!
 */

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Content = styled2.div<{
	children?: ReactNode;
}>({
	display: 'block',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Column = styled2.div<{
	children?: ReactNode;
}>({
	display: 'flex',
	alignItems: 'center',
	textAlign: 'center',
	justifyContent: 'center',
	flexDirection: 'column',
	marginTop: token('space.100'),
	marginRight: 'auto',
	marginBottom: token('space.100'),
	marginLeft: 'auto',
	paddingTop: 0,
	paddingRight: token('space.200'),
	paddingBottom: 0,
	paddingLeft: token('space.200'),
	overflowX: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ImgWrapper = styled2.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${15 * gridSize}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled2.p<{
	children?: ReactNode;
}>({
	marginTop: token('space.100'),
	/* Text does not wrap in IE11 unless an explicit width is defined. */
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CoverAllOfParent = styled.div<{
	children?: ReactNode;
}>({
	position: 'absolute',
	top: 0,
	bottom: 0,
	left: 0,
	right: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Container: ComponentType<{
	children?: ReactNode;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
}> = styled(CoverAllOfParent)({
	zIndex: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const MarginTop: ComponentType<{
	children?: ReactNode;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
}> = styled(MarginBottom)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: `${marginMax}px`,
});

// FIXME: convert-props-syntax doesn't catch this
// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
const dropzoneMixin = css<{ isDraggingOver?: boolean }>`
	border-width: 2px;
	border-style: dashed;
	border-radius: ${fieldBorderRadius};
	border-color: ${dropzoneBorderColor};
	transition:
		border-color ${transitionSpeed},
		background-color ${transitionSpeed};

	background-color: ${(props: { isDraggingOver?: boolean }) =>
		props.isDraggingOver ? dropzoneBackgroundHoverColor : dropzoneBackgroundColor};
`;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ColorsRemove: ComponentType<{
	children?: ReactNode;
	dropHighlight?: boolean;
	isDraggingOver?: boolean;
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
}> = styled(CoverAllOfParent)(
	{
		display: 'flex',
		flexDirection: 'column',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text.subtle'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	(props: { dropHighlight?: boolean }) =>
		props.dropHighlight
			? // FIXME: type error in object syntax passing result of `css` to `css`
				// WORKAROUND: just return `dropzoneMixin` without `css()`
				// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css`
					${dropzoneMixin};
				`
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css({
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					backgroundColor: token('color.background.danger'),
				}),
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Img = styled2.img<{
	children?: ReactNode;
	src: string;
	alt: string;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${15 * gridSize}px`,
});
